import React, { useState, useEffect } from 'react';
import { contactAPI } from '../api';

function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [formData, setFormData] = useState({ phone_number: '', name: '', tags: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Selection and filter states
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'new', 'contacted'
  
  // CSV Import states
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [importStep, setImportStep] = useState('upload'); // 'upload' or 'mapping'
  const [columnMapping, setColumnMapping] = useState({ name: '', phone: '' });
  const [commonTag, setCommonTag] = useState('');

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const response = await contactAPI.getAll();
      setContacts(response.data);
    } catch (error) {
      console.error('Error loading contacts:', error);
      setError('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await contactAPI.create(formData);
      setShowModal(false);
      setFormData({ phone_number: '', name: '', tags: '' });
      setSuccess('Contact added successfully');
      loadContacts();
    } catch (error) {
      console.error('Error creating contact:', error);
      setError(error.response?.data?.error || 'Failed to create contact');
    }
  };

  const handleBulkImport = async (e) => {
    e.preventDefault();
    try {
      // Map CSV data to contacts using column mapping
      const contacts = csvData.map(row => {
        const contact = {
          phone_number: row[columnMapping.phone] || '',
          name: row[columnMapping.name] || '',
          tags: commonTag
        };
        return contact;
      }).filter(c => c.phone_number); // Only include rows with phone numbers

      await contactAPI.createBulk(contacts);
      
      // Reset state
      setShowBulkModal(false);
      setCsvFile(null);
      setCsvData([]);
      setCsvHeaders([]);
      setImportStep('upload');
      setColumnMapping({ name: '', phone: '' });
      setCommonTag('');
      
      setSuccess(`${contacts.length} contacts imported successfully`);
      loadContacts();
    } catch (error) {
      console.error('Error importing contacts:', error);
      setError('Failed to import contacts');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if it's a CSV file
    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setCsvFile(file);
    
    // Read and parse CSV
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      parseCSV(text);
    };
    reader.readAsText(file);
  };

  const parseCSV = (text) => {
    try {
      const lines = text.trim().split('\n');
      if (lines.length === 0) {
        setError('CSV file is empty');
        return;
      }

      // Parse headers (first row)
      const headers = lines[0].split(',').map(h => h.trim());
      setCsvHeaders(headers);

      // Parse data rows
      const data = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim());
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        return row;
      });

      setCsvData(data);
      setImportStep('mapping');
      setError('');
    } catch (err) {
      setError('Failed to parse CSV file');
      console.error('CSV parsing error:', err);
    }
  };

  const resetBulkImport = () => {
    setShowBulkModal(false);
    setCsvFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setImportStep('upload');
    setColumnMapping({ name: '', phone: '' });
    setCommonTag('');
    setError('');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await contactAPI.delete(id);
        setSuccess('Contact deleted');
        setSelectedContacts([]);
        loadContacts();
      } catch (error) {
        console.error('Error deleting contact:', error);
        setError('Failed to delete contact');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedContacts.length === 0) {
      setError('No contacts selected');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete ${selectedContacts.length} contact(s)?`)) {
      try {
        await contactAPI.bulkDelete(selectedContacts);
        setSuccess(`${selectedContacts.length} contact(s) deleted`);
        setSelectedContacts([]);
        loadContacts();
      } catch (error) {
        console.error('Error deleting contacts:', error);
        setError('Failed to delete contacts');
      }
    }
  };

  const toggleContactSelection = (id) => {
    setSelectedContacts(prev => 
      prev.includes(id) ? prev.filter(cid => cid !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedContacts.length === filteredContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(filteredContacts.map(c => c.id));
    }
  };

  // Filter contacts based on search text and filter type
  const filteredContacts = contacts.filter(contact => {
    // Text filter
    const matchesText = !filterText || 
      contact.name?.toLowerCase().includes(filterText.toLowerCase()) ||
      contact.phone_number?.includes(filterText) ||
      contact.tags?.toLowerCase().includes(filterText.toLowerCase());
    
    // Type filter
    let matchesType = true;
    if (filterType === 'new') {
      matchesType = contact.has_been_contacted === 0;
    } else if (filterType === 'contacted') {
      matchesType = contact.has_been_contacted === 1;
    }
    
    return matchesText && matchesType;
  });

  if (loading) {
    return <div className="container"><div className="loading">Loading contacts...</div></div>;
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Contacts</h1>
        <div className="button-group">
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            Add Contact
          </button>
          <button className="btn btn-secondary" onClick={() => setShowBulkModal(true)}>
            Bulk Import
          </button>
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {/* Filters and bulk actions */}
      <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search contacts..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            style={{ flex: '1', minWidth: '200px', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px' }}
          >
            <option value="all">All Contacts</option>
            <option value="new">New Only</option>
            <option value="contacted">Contacted Only</option>
          </select>
          {selectedContacts.length > 0 && (
            <button 
              className="btn btn-danger"
              onClick={handleBulkDelete}
              style={{ marginLeft: 'auto' }}
            >
              Delete Selected ({selectedContacts.length})
            </button>
          )}
        </div>
      </div>

      <div className="card">
        {contacts.length === 0 ? (
          <div className="empty-state">
            <h3>No contacts yet</h3>
            <p>Add contacts to start sending campaigns!</p>
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="empty-state">
            <h3>No contacts match your filters</h3>
            <p>Try adjusting your search or filter criteria.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={selectedContacts.length === filteredContacts.length && filteredContacts.length > 0}
                      onChange={toggleSelectAll}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                  </th>
                  <th>Name</th>
                  <th>Phone Number</th>
                  <th>Tags</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
            <tbody>
              {filteredContacts.map(contact => (
                <tr key={contact.id} style={{ backgroundColor: selectedContacts.includes(contact.id) ? '#f0f8ff' : 'transparent' }}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedContacts.includes(contact.id)}
                      onChange={() => toggleContactSelection(contact.id)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {contact.name || '-'}
                      {contact.has_been_contacted === 0 && (
                        <span style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          backgroundColor: '#4CAF50',
                          color: 'white',
                          borderRadius: '10px',
                          fontWeight: 'bold'
                        }}>NEW</span>
                      )}
                    </div>
                  </td>
                  <td>{contact.phone_number}</td>
                  <td>{contact.tags || '-'}</td>
                  <td>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: contact.has_been_contacted === 0 ? '#e8f5e9' : '#fff3e0',
                      color: contact.has_been_contacted === 0 ? '#2e7d32' : '#e65100'
                    }}>
                      {contact.has_been_contacted === 0 ? 'Not Contacted' : 'Contacted'}
                    </span>
                  </td>
                  <td>{new Date(contact.created_at).toLocaleDateString()}</td>
                  <td>
                    <button className="btn btn-danger" onClick={() => handleDelete(contact.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>          </div>        )}
      </div>

      {showModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Add New Contact</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="text"
                  required
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  placeholder="e.g., 9876543210 or 919876543210"
                />
                <small style={{ color: '#666' }}>
                  10-digit numbers will automatically get 91 prefix added (e.g., 9876543210 → 919876543210)
                </small>
              </div>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter contact name"
                />
              </div>
              <div className="form-group">
                <label>Tags</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="e.g., customer, vip"
                />
              </div>
              <div className="button-group">
                <button type="submit" className="btn btn-primary">Add Contact</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBulkModal && (
        <div className="modal">
          <div className="modal-content" style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2>Bulk Import Contacts</h2>
              <button className="close-btn" onClick={resetBulkImport}>&times;</button>
            </div>

            {importStep === 'upload' && (
              <div>
                <div className="form-group">
                  <label>Upload CSV File</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    style={{ 
                      padding: '10px',
                      border: '2px dashed #ccc',
                      borderRadius: '5px',
                      width: '100%',
                      cursor: 'pointer'
                    }}
                  />
                  <small style={{ color: '#666', display: 'block', marginTop: '10px' }}>
                    Upload a CSV file with contact information. The first row should contain column headers.
                  </small>
                </div>

                {csvFile && (
                  <div style={{ 
                    marginTop: '15px', 
                    padding: '10px', 
                    backgroundColor: '#e8f5e9',
                    borderRadius: '5px'
                  }}>
                    <strong>✓ File uploaded:</strong> {csvFile.name}
                    <div style={{ marginTop: '5px', fontSize: '14px' }}>
                      Found {csvData.length} rows with columns: {csvHeaders.join(', ')}
                    </div>
                  </div>
                )}

                <div style={{ 
                  marginTop: '20px',
                  padding: '15px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '5px'
                }}>
                  <strong>📋 CSV Format Example:</strong>
                  <pre style={{ 
                    marginTop: '10px',
                    padding: '10px',
                    backgroundColor: 'white',
                    borderRadius: '3px',
                    fontSize: '13px',
                    overflow: 'auto'
                  }}>
{`Name,Phone,Tags
John Doe,9876543210,customer
Jane Smith,8765432109,vip
Bob Wilson,7654321098,lead`}
                  </pre>
                  <small style={{ color: '#666', display: 'block', marginTop: '8px' }}>
                    • Column names can be anything (you'll map them in the next step)
                    <br />
                    • 10-digit numbers will automatically get 91 prefix (9876543210 → 919876543210)
                    <br />
                    • Spaces, special characters, and leading zeros are automatically removed
                  </small>
                </div>

                <div className="button-group" style={{ marginTop: '20px' }}>
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={() => {
                      if (!csvFile || csvData.length === 0) {
                        setError('Please upload a valid CSV file first');
                        return;
                      }
                      setImportStep('mapping');
                    }}
                    disabled={!csvFile || csvData.length === 0}
                  >
                    Next: Map Columns
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={resetBulkImport}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {importStep === 'mapping' && (
              <form onSubmit={handleBulkImport}>
                <div style={{ 
                  padding: '15px',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '5px',
                  marginBottom: '20px'
                }}>
                  <strong>📊 Found {csvData.length} contacts in {csvFile?.name}</strong>
                  <div style={{ fontSize: '14px', marginTop: '5px' }}>
                    Map your CSV columns to contact fields below
                  </div>
                </div>

                <div className="form-group">
                  <label>Phone Number Column *</label>
                  <select
                    required
                    value={columnMapping.phone}
                    onChange={(e) => setColumnMapping({ ...columnMapping, phone: e.target.value })}
                    style={{ width: '100%', padding: '8px' }}
                  >
                    <option value="">-- Select Column --</option>
                    {csvHeaders.map((header, index) => (
                      <option key={index} value={header}>{header}</option>
                    ))}
                  </select>
                  <small style={{ color: '#666' }}>
                    Select the column containing phone numbers (will be standardized to 91XXXXXXXXXX format)
                  </small>
                </div>

                <div className="form-group">
                  <label>Name Column</label>
                  <select
                    value={columnMapping.name}
                    onChange={(e) => setColumnMapping({ ...columnMapping, name: e.target.value })}
                    style={{ width: '100%', padding: '8px' }}
                  >
                    <option value="">-- Select Column (Optional) --</option>
                    {csvHeaders.map((header, index) => (
                      <option key={index} value={header}>{header}</option>
                    ))}
                  </select>
                  <small style={{ color: '#666' }}>
                    Select the column containing contact names (optional)
                  </small>
                </div>

                <div className="form-group">
                  <label>Tag (Applied to All Contacts)</label>
                  <input
                    type="text"
                    value={commonTag}
                    onChange={(e) => setCommonTag(e.target.value)}
                    placeholder="e.g., campaign-jan-2024, leads, customers"
                    style={{ width: '100%', padding: '8px' }}
                  />
                  <small style={{ color: '#666' }}>
                    This tag will be applied to all imported contacts
                  </small>
                </div>

                {/* Preview */}
                {columnMapping.phone && (
                  <div style={{ 
                    marginTop: '20px',
                    padding: '15px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '5px',
                    maxHeight: '300px',
                    overflow: 'auto'
                  }}>
                    <strong>📋 Preview (First 5 rows):</strong>
                    <table className="table" style={{ marginTop: '10px', fontSize: '13px' }}>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Phone Number</th>
                          <th>Tag</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvData.slice(0, 5).map((row, index) => (
                          <tr key={index}>
                            <td>{row[columnMapping.name] || '-'}</td>
                            <td>{row[columnMapping.phone] || '-'}</td>
                            <td>{commonTag || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {csvData.length > 5 && (
                      <small style={{ color: '#666' }}>
                        ... and {csvData.length - 5} more rows
                      </small>
                    )}
                  </div>
                )}

                <div className="button-group" style={{ marginTop: '20px' }}>
                  <button type="submit" className="btn btn-primary">
                    Import {csvData.length} Contacts
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setImportStep('upload')}
                  >
                    Back
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={resetBulkImport}>
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Contacts;
